import { Card, CardHeader, Stack, Divider, Typography, Box, Button, Grid, TextField, FormControl, Select, MenuItem, RadioGroup, Radio, FormControlLabel, FormLabel, Slider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Slide, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState, useEffect, forwardRef } from 'react';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import PropTypes from 'prop-types';
import axiosInstance from '../../../hooks/axiosInstance';

const CHART_HEIGHT = 460;
const LEGEND_HEIGHT = 72;
const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
const StyledChartWrapper = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(2),
  '& .apexcharts-canvas svg': {
    height: CHART_HEIGHT,
  },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    alignContent: 'center',
    position: 'relative !important',
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));

AppCurrentSubject.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  subheader2: PropTypes.string,
  deviceId: PropTypes.string,
  jwtToken: PropTypes.string,
};

export default function AppCurrentSubject({ title, color="info", subheader, subheader2, deviceId, jwtToken }) {

  // --- Light Spectrum Control States ---
  const [presetMode, setPresetMode] = useState('1');
  const [manualValues, setManualValues] = useState({ PAR: 50, Red: 50, Blue: 50, UV: 50, IR: 50 });

  // --- Condition Setter States ---
  const [setpoinValue, setsetpoinValue] = useState({ temperature: '', humidity: '', intensity: '', co2: '', daynight_mode: '' });
  const [open, setOpen] = useState(false);
  const [msg, setmsg] = useState('');
  const [error, seterror] = useState(null);
  let callbackTimeout;

  useEffect(() => {
    const savedSetpointValue = localStorage.getItem('setpoinValue');
    if (savedSetpointValue) setsetpoinValue(JSON.parse(savedSetpointValue));
  }, []);

  useEffect(() => {
    localStorage.setItem('setpoinValue', JSON.stringify(setpoinValue));
  }, [setpoinValue]);

  const handleSliderChange = (color) => (event, newValue) => {
    setManualValues((prev) => ({ ...prev, [color]: newValue }));
  };

  const getColor = (color) => {
    switch (color.toLowerCase()) {
      case 'par': return '#FFF176';
      case 'red': return '#EF9A9A';
      case 'blue': return '#90CAF9';
      case 'uv': return '#BA68C8';
      case 'ir': return '#D32F2F';
      default: return '#9e9e9e'; // grey fallback
    }
  };


  const setter = () => {
    let PARLight = 0;
    let REDLight = 0;
    let BLUELight = 0;
    let UVLight = 0;
    let IRLight = 0;

    if (presetMode === "0"){
      PARLight = manualValues.PAR;
      REDLight = manualValues.Red;
      BLUELight = manualValues.Blue;
      UVLight = manualValues.UV;
      IRLight = manualValues.IR;
    }

    axiosInstance
      .post(`/condition/setpoint/${deviceId}?jwt_token=${jwtToken}`, {
        daynight_mode: setpoinValue.daynight_mode,
        temperature: parseFloat(setpoinValue.temperature),
        humidity: parseFloat(setpoinValue.humidity),
        intensity: parseFloat(setpoinValue.intensity),
        co2: parseFloat(setpoinValue.co2),
        light_mode: presetMode,
        light_par: parseFloat(PARLight),
        light_red: parseFloat(REDLight),
        light_blue: parseFloat(BLUELight),
        light_uv: parseFloat(UVLight),
        light_ir: parseFloat(IRLight),
      })
      .then((res) => {
        if (res.status === 200) {
          clearTimeout(callbackTimeout);
          seterror(2);
          setmsg('Waiting for device processing...');
          const subEventSource = new EventSource(
            `https://demo.smartfarm.id/condition/get-events-callback/${deviceId}?jwt_token=${jwtToken}`
          );
          subEventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.message === "Setpoint settings received") {
              setmsg('Set point settings received');
              seterror(0);
              subEventSource.close();
              clearTimeout(callbackTimeout);
            }
            callbackTimeout = setTimeout(() => {
              setmsg('Set point sent but not received well, try again later');
              seterror(1);
              subEventSource.close();
            }, 12000);
          };
        }
      })
      .catch((err) => {
        setmsg(err.response?.data?.msg || 'Unknown error');
        seterror(1);
      });
    setOpen(true);
  };

  return (
    <Card sx={{ p: 3,         
      color: (theme) => theme.palette[color].darker,
      bgcolor: (theme) => theme.palette[color].lighter }}>
      <CardHeader title={title} subheader={subheader} />

      {/* Light Spectrum Control Section */}
      <Stack spacing={3}>
        <Box textAlign="center">
          {/* Condition Setter Section */}
          <Typography variant="h5">Condition Setter</Typography>
          <Typography variant="h9">To submit the setpoint, all parameters must be filled in</Typography>

          <Stack mt ={2}>
            <Grid container spacing={2} justifyContent="center" mb={2}>
              <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="Temperature"
                    label="Temperature (°C)"
                    type="number"
                    value={setpoinValue.temperature}
                    onChange={(e) => setsetpoinValue({ ...setpoinValue, temperature: e.target.value })}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": { backgroundColor: "white" }
                    }}
                  />
              </Grid>
              <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="Humidity"
                    label="Humidity (%)"
                    type="number"
                    value={setpoinValue.humidity}
                    onChange={(e) => setsetpoinValue({ ...setpoinValue, humidity: e.target.value })}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": { backgroundColor: "white" }
                    }}
                  />
              </Grid>
              <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="PAR Light Intensity"
                    label="PAR Light Intensity (LUX)"
                    type="number"
                    value={setpoinValue.intensity}
                    onChange={(e) => setsetpoinValue({ ...setpoinValue, intensity: e.target.value })}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": { backgroundColor: "white" }
                    }}
                  />
              </Grid>
              <Grid item xs={6}>
                  <TextField
                    fullWidth
                    name="CO2"
                    label="CO₂ (ppm)"
                    type="number"
                    value={setpoinValue.co2}
                    onChange={(e) => setsetpoinValue({ ...setpoinValue, co2: e.target.value })}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": { backgroundColor: "white" }
                    }}
                  />
              </Grid>
            </Grid>

            <FormControl fullWidth mb={2}>
              <InputLabel>Set for Day/ Night</InputLabel>
              <Select
                value={setpoinValue.daynight_mode}
                onChange={(e) => setsetpoinValue({ ...setpoinValue, daynight_mode: e.target.value })}
                sx ={{backgroundColor: "white",borderRadius: "8px","& .MuiInputBase-root": {backgroundColor: "white"}}}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Night">Night</MenuItem>
              </Select>
            </FormControl>

            <Stack spacing={2} mb={2} mt={2}>
              <FormControl fullWidth>
                <FormLabel>Choose Light Spectrum Mode</FormLabel>
                <Select value={presetMode} onChange={(e) => setPresetMode(e.target.value)}
                  sx ={{backgroundColor: "white",borderRadius: "8px","& .MuiInputBase-root": {backgroundColor: "white"}}}>
                  <MenuItem value="0">Mode 0: Manual Control</MenuItem>
                  <MenuItem value="1">Mode 1: PAR only</MenuItem>
                  <MenuItem value="2">Mode 2: PAR + Red Spectrum</MenuItem>
                  <MenuItem value="3">Mode 3: PAR + Blue Spectrum</MenuItem>
                  <MenuItem value="4">Mode 4: PAR + Red + Blue + UV + IR</MenuItem>
                  <MenuItem value="5">Mode 5: All LEDs Off</MenuItem>
                </Select>
              </FormControl>

              {presetMode === "0" && (
                <>
                  {Object.keys(manualValues).map((color) => (
                    <Box key={color}>
                      <Typography gutterBottom>{color} %</Typography>
                      <Slider
                        value={manualValues[color]}
                        onChange={handleSliderChange(color)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={100}
                        sx={{
                          color: getColor(color)
                        }}
                      />
                    </Box>
                  ))}
                </>
              )}
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={setter}
              disabled={Object.values(setpoinValue).some((val) => val.length === 0)}
            >
              Set Condition
            </Button>
            <Typography variant="caption" color="textSecondary">{subheader2}</Typography>
          </Stack>
        </Box>
      </Stack>

      {/* Dialog for Feedback */}
      <Dialog open={open} TransitionComponent={Transition} keepMounted onClose={() => setOpen(false)}>
        <DialogTitle>{error === 1 ? "Failure" : error === 0 ? "Success" : "Loading..."}</DialogTitle>
        <DialogContent>
          <DialogContentText>{msg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}